import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";

type Period = "minute" | "hour" | "day" | "week" | "month";

const PERIODS: { value: Period; label: string }[] = [
  { value: "minute", label: "Minute" },
  { value: "hour",   label: "Hour" },
  { value: "day",    label: "Day" },
  { value: "week",   label: "Week" },
  { value: "month",  label: "Month" },
];

const pad = (n: number) => String(n).padStart(2, "0");

// Replicates SQLite's strftime('%Y-W%W') — week starts Monday, range 00–53
function sqliteWeekStr(d: Date): string {
  const year = d.getUTCFullYear();
  const jan1 = new Date(Date.UTC(year, 0, 1));
  const jan1Dow = jan1.getUTCDay();
  const dayOfYear = Math.floor((d.getTime() - jan1.getTime()) / 86_400_000);
  const daysToFirstMon = jan1Dow === 1 ? 0 : jan1Dow === 0 ? 1 : 8 - jan1Dow;
  const weekNum = dayOfYear < daysToFirstMon ? 0 : Math.floor((dayOfYear - daysToFirstMon) / 7) + 1;
  return `${year}-W${pad(weekNum)}`;
}

const todayStr = () => new Date().toISOString().split("T")[0];

function defaultStartDate(p: Period): string {
  const d = new Date();
  if (p === "week") {
    const dow = d.getUTCDay();
    d.setUTCDate(d.getUTCDate() - (dow === 0 ? 6 : dow - 1));
    return d.toISOString().split("T")[0];
  }
  if (p === "month") {
    d.setUTCDate(1);
    return d.toISOString().split("T")[0];
  }
  return d.toISOString().split("T")[0];
}

function computedEndDate(period: Period, startDate: string): string {
  const d = new Date(startDate + "T00:00:00Z");
  if (period === "week") d.setUTCDate(d.getUTCDate() + 6);
  else if (period === "month") d.setUTCDate(d.getUTCDate() + 29);
  return d.toISOString().split("T")[0];
}

// Each period shows 1 window at finer resolution:
//   minute → 60 s, hour → 60 min, day → 24 h, week → 7 days, month → 4–5 weeks
function generateSeries(
  raw: { label: string; count: number }[],
  period: Period,
  startDate: string,
): { label: string; display: string; count: number }[] {
  const map = new Map(raw.map((d) => [d.label, d.count]));
  const pts: { label: string; display: string; count: number }[] = [];
  // hour period passes "YYYY-MM-DD HH:00:00"; others pass "YYYY-MM-DD"
  const start = startDate.includes(" ")
    ? new Date(startDate.replace(" ", "T") + "Z")
    : new Date(startDate + "T00:00:00Z");

  if (period === "minute") {
    for (let i = 0; i < 60; i++) {
      const t = new Date(start.getTime() + i * 1_000);
      const label = `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())} ${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}:${pad(t.getUTCSeconds())}`;
      pts.push({ label, display: `${pad(i)}s`, count: map.get(label) ?? 0 });
    }
    return pts;
  }
  if (period === "hour") {
    for (let i = 0; i < 60; i++) {
      const t = new Date(start.getTime() + i * 60_000);
      const label = `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())} ${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}`;
      pts.push({ label, display: `${pad(t.getHours())}:${pad(t.getMinutes())}`, count: map.get(label) ?? 0 });
    }
    return pts;
  }
  if (period === "day") {
    for (let i = 0; i < 24; i++) {
      const t = new Date(start.getTime() + i * 3_600_000);
      const label = `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())} ${pad(t.getUTCHours())}`;
      pts.push({ label, display: `${pad(i)}:00`, count: map.get(label) ?? 0 });
    }
    return pts;
  }
  if (period === "week") {
    const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    for (let i = 0; i < 7; i++) {
      const t = new Date(start.getTime() + i * 86_400_000);
      const label = `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
      pts.push({ label, display: DAYS[t.getUTCDay()], count: map.get(label) ?? 0 });
    }
    return pts;
  }
  // month
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const seen = new Set<string>();
  for (let i = 0; i < 35; i++) {
    const t = new Date(start.getTime() + i * 86_400_000);
    const wk = sqliteWeekStr(t);
    if (!seen.has(wk)) {
      seen.add(wk);
      pts.push({ label: wk, display: `${MONTHS[t.getUTCMonth()]} ${t.getUTCDate()}`, count: map.get(wk) ?? 0 });
    }
  }
  return pts;
}

export function OrgAnalyticsPage({ orgId, onBack }: { orgId: number; onBack: () => void }) {
  const [period, setPeriod] = useState<Period>("day");
  const [startDate, setStartDate] = useState<string>(() => defaultStartDate("day"));
  const [userHasSetStartDate, setUserHasSetStartDate] = useState(false);

  useEffect(() => {
    if (!userHasSetStartDate) setStartDate(defaultStartDate(period));
  }, [period, userHasSetStartDate]);

  const endDate = computedEndDate(period, startDate);

  // For "hour", pass current UTC hour so the backend queries the right 60-minute window
  const effectiveStart = period === "hour"
    ? `${startDate} ${pad(new Date().getUTCHours())}:00:00`
    : startDate;

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.orgAnalytics(orgId, period, effectiveStart),
    queryFn: () => api.getOrgAnalytics(orgId, period, effectiveStart),
  });

  const chartData = data ? generateSeries(data.screening_over_time, period, effectiveStart) : [];
  const s = data?.summary;
  const tth = data?.time_to_hire;
  const total = s?.total_screened ?? 0;
  const hireRate = total > 0 ? Math.round(((s?.strong_hire_count ?? 0) / total) * 100) : 0;
  const maxFail = Math.max(...(data?.failure_reasons ?? []).map((r) => r.fail_count), 1);

  const inputBase = "rounded-btn border border-border-card px-2 py-1 text-xs focus:outline-none";
  const inputActive = "bg-white text-near-black focus:shadow-focus";
  const inputDisabled = "bg-surface-secondary text-text-secondary cursor-not-allowed";

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 bg-white px-3 py-4 shadow-sm sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-btn text-text-secondary hover:bg-surface-secondary hover:text-near-black"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
              </svg>
            </button>
            <div>
              <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">
                {data?.org.name ?? "Organization"} Analytics
              </h2>
              <p className="text-sm text-text-secondary">Activity overview and hiring insights</p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl space-y-6 px-3 py-6 sm:px-6">
          {isLoading && <p className="text-sm text-text-secondary">Loading analytics…</p>}
          {error && <p className="text-sm text-error-text">Failed to load analytics.</p>}

          {data && (
            <>
              {/* Summary stat cards */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard label="Total Screened" value={total} />
                <StatCard label="Strong Hire Rate" value={`${hireRate}%`} accent />
                <StatCard label="Avg Match Score" value={s?.avg_match_score != null ? `${s.avg_match_score}%` : "—"} />
                <StatCard label="Considered" value={s?.consider_count ?? 0} />
              </div>

              {/* Screening over time */}
              <div className="rounded-card bg-white p-5 shadow-card">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-near-black">Candidates Screened Over Time</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Start date */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-text-secondary">From</span>
                      <input
                        type="date"
                        value={startDate}
                        max={todayStr()}
                        onChange={(e) => {
                          if (e.target.value) {
                            setStartDate(e.target.value);
                            setUserHasSetStartDate(true);
                          }
                        }}
                        className={`${inputBase} ${inputActive}`}
                      />
                    </div>
                    {/* End date — computed from period + start, always read-only */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-text-secondary">To</span>
                      <input
                        type="date"
                        value={endDate}
                        disabled
                        className={`${inputBase} ${inputDisabled}`}
                      />
                    </div>
                    {/* Period toggle */}
                    <div className="flex items-center gap-1 rounded-btn border border-border-card bg-surface-secondary p-0.5">
                      {PERIODS.map((p) => (
                        <button
                          key={p.value}
                          onClick={() => setPeriod(p.value)}
                          className={[
                            "rounded-btn px-2.5 py-1 text-xs font-medium transition-colors",
                            period === p.value
                              ? "bg-white text-near-black shadow-sm"
                              : "text-text-secondary hover:text-near-black",
                          ].join(" ")}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {chartData.length === 0 ? (
                  <p className="py-10 text-center text-sm italic text-text-secondary">No screening data in this period.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <ReLineChart data={chartData} margin={{ top: 5, right: 16, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis
                        dataKey="display"
                        tick={{ fontSize: 10, fill: "#9ca3af" }}
                        tickLine={false}
                        axisLine={{ stroke: "#e5e7eb" }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: "#9ca3af" }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                        labelStyle={{ color: "#6b7280", fontWeight: 500 }}
                        itemStyle={{ color: "#222222" }}
                        formatter={(val) => [val, "Screened"]}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#222222"
                        strokeWidth={2}
                        dot={{ fill: "#222222", r: 3, strokeWidth: 0 }}
                        activeDot={{ fill: "#ff385c", r: 5, strokeWidth: 0 }}
                      />
                    </ReLineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Bottom row: time to hire + failure reasons */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Time to hire */}
                <div className="rounded-card bg-white p-5 shadow-card">
                  <h3 className="mb-4 text-sm font-semibold text-near-black">Duration: Screened → Hired</h3>
                  {tth && tth.count > 0 ? (
                    <div className="space-y-4">
                      <div className="text-center">
                        <p className="text-[42px] font-bold tabular-nums text-near-black leading-none">
                          {tth.avg_days ?? "—"}
                        </p>
                        <p className="mt-1 text-sm text-text-secondary">avg days to hire</p>
                      </div>
                      <div className="flex justify-between border-t border-surface-secondary pt-3 text-center">
                        <div>
                          <p className="text-lg font-bold tabular-nums text-near-black">{tth.min_days ?? "—"}</p>
                          <p className="text-[10px] text-text-secondary">Fastest</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-near-black">{tth.count}</p>
                          <p className="text-[10px] text-text-secondary">Total Hires</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-near-black">{tth.max_days ?? "—"}</p>
                          <p className="text-[10px] text-text-secondary">Longest</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="py-6 text-center text-sm italic text-text-secondary">
                      No hires tracked yet. Stage transitions to "hired" will appear here.
                    </p>
                  )}
                </div>

                {/* Failure reasons */}
                <div className="rounded-card bg-white p-5 shadow-card">
                  <h3 className="mb-4 text-sm font-semibold text-near-black">Common Rejection Criteria</h3>
                  {data.failure_reasons.length === 0 ? (
                    <p className="py-6 text-center text-sm italic text-text-secondary">
                      No rejection data yet. Matrix scores for rejected candidates will appear here.
                    </p>
                  ) : (
                    <div className="space-y-2.5">
                      {data.failure_reasons.map((r) => (
                        <div key={r.criterion}>
                          <div className="mb-1 flex items-center justify-between text-xs">
                            <span className="font-medium text-near-black truncate max-w-[60%]">{r.criterion}</span>
                            <span className="text-text-secondary tabular-nums">
                              {r.fail_count} rejections · avg {r.avg_score}%
                            </span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-surface-secondary">
                            <div
                              className="h-2 rounded-full bg-rausch transition-all"
                              style={{ width: `${(r.fail_count / maxFail) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Recommendation breakdown */}
              <div className="rounded-card bg-white p-5 shadow-card">
                <h3 className="mb-4 text-sm font-semibold text-near-black">Recommendation Breakdown</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="rounded-btn bg-emerald-50 py-4">
                    <p className="text-2xl font-bold tabular-nums text-emerald-700">{s?.strong_hire_count ?? 0}</p>
                    <p className="mt-1 text-xs font-medium text-emerald-600">Strong Hire</p>
                  </div>
                  <div className="rounded-btn bg-amber-50 py-4">
                    <p className="text-2xl font-bold tabular-nums text-amber-700">{s?.consider_count ?? 0}</p>
                    <p className="mt-1 text-xs font-medium text-amber-600">Consider</p>
                  </div>
                  <div className="rounded-btn bg-rose-50 py-4">
                    <p className="text-2xl font-bold tabular-nums text-error-text">{s?.reject_count ?? 0}</p>
                    <p className="mt-1 text-xs font-medium text-error-text">Rejected</p>
                  </div>
                </div>
                {total > 0 && (
                  <div className="mt-3 flex h-3 overflow-hidden rounded-full">
                    <div
                      className="bg-emerald-500 transition-all"
                      style={{ width: `${((s?.strong_hire_count ?? 0) / total) * 100}%` }}
                    />
                    <div
                      className="bg-amber-400 transition-all"
                      style={{ width: `${((s?.consider_count ?? 0) / total) * 100}%` }}
                    />
                    <div
                      className="bg-rausch transition-all"
                      style={{ width: `${((s?.reject_count ?? 0) / total) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string | number; accent?: boolean }) {
  return (
    <div className="rounded-card bg-white p-4 shadow-card">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${accent ? "text-rausch" : "text-near-black"}`}>{value}</p>
    </div>
  );
}
